import {
  AfterViewInit,
  Component,
  Directive,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  AllowedCharsOptions,
  DuplicateStringOptions,
  FormFieldConfig,
  RangeOptions,
} from '@app/shared/forms/dynamic-form/fields.service';
import { FieldType } from '@app/origami-api/constants/field-type';
import { FormFieldLabelComponent } from '../form-field-label/form-field-label.component';
import { Field, ServerBoundaries } from '@app/origami-api/constants';
import { SpecialValidationType } from '@app/origami-api/constants/validation-type';
import { getSpecialValidatorFn } from '@app/shared/utils/special-validators';
import { tiStringsFormat } from '@app/shared/pipes/string-format.pipe';
import { DateFormatStore } from '@app/session/date-format-store';
import { DateInputFormatPlaceholder } from '@progress/kendo-angular-dateinputs';
import { RoomDateFormatPipe } from '@app/shared/pipes/room-date-format.pipe';
import { FakeFormControl, SpecialValidatorFn } from '@app/shared/utils/special-validators/special-validators.model';
import { TooltipOptions } from '@tpt/common/tooltips/overflow';

/*
I think we need to use this component only to inherit general entities(like config, formGroup, etc..).
Don't use it to generate form control (create custom which will inherit this).
 */

//! important: should be export, because in AOT don't work default hooks from class below in inherited further components
@Directive()
export class FormInputBase implements OnInit, AfterViewInit, OnDestroy {
  readonly baseComponentStrings = tiStrings.shared.dynamicForms.dynamicFields.formInputBase;
  @Input() name: string;
  @Input() config: FormFieldConfig;
  @Input() formGroup: UntypedFormGroup;
  @Input() valueChange$: Subscription;
  @Input() customChange$: Subscription;
  @Input() isReadonly: boolean = false;

  @Output() valueChangeEmitter = new EventEmitter();
  @Output() formRebuildRequestEmitter = new EventEmitter();

  @ViewChild(FormFieldLabelComponent) labelComponent: FormFieldLabelComponent;
  public element: HTMLElement = null;

  public serverBoundaries = ServerBoundaries;

  public FieldType = FieldType;

  public isBusy: boolean = false;

  public isHidden: boolean;

  /*
    This property is used to handle default values from the custom form.
    At present we are only allow to set default values for custom fields.
    That's why I'm adding corresponding logic only to a limited set of field components.
   */
  public get isMasked(): boolean {
    return !!(this.config.UIData && this.config.UIData.defaultValueFromCustomForm);
  }

  public forceDisabled: boolean = false;

  public isClearButtonHidden(): boolean {
    return this.self?.value === undefined || this.self?.value === null || this.self?.value === '';
  }

  protected formChange$: Subscription;
  protected selfChange$: Subscription;

  private copyClick$: Subscription;

  public self: AbstractControl;

  public displayValue: string;
  public originalValue: any;
  appendTo: 'root' | 'component' = 'component';

  // Shows a popover with the full text only when the input value is truncated.
  readonly overflowTooltipOptions: Partial<TooltipOptions> = { mode: 'white-background', static: false };

  private roomDateFormatPipe = inject(RoomDateFormatPipe);

  onCopyClickEvent = (copyEvent: { fieldName: string; isSelected: boolean }) => {
    const copyPasteState = this.config.UIData.copyPasteState;

    copyEvent.isSelected
      ? (copyPasteState.copiedFields[copyEvent.fieldName] = this.formGroup.controls[copyEvent.fieldName].value)
      : delete copyPasteState.copiedFields[copyEvent.fieldName]; // TODO: probably key word 'delete' is a bad approach
    copyPasteState.change.update((value) => !value); //toggle change
  };

  isDisabled = () => this.self && this.self.disabled;

  ngOnInit(): void {
    this.config.customFieldSettings = this.config.customFieldSettings || {};
    const name = this.name || this.config.fieldName;
    this.self = this.formGroup.get(name) || this.formGroup.controls[name];
    this.originalValue = this.self?.value;
    this.appendTo = this.config?.UIData?.appendTo ?? 'root';
  }

  getControlElement(containerForChoosing?: string) {
    const elementId = this.config.fieldName;
    // temporary solution. implement universal interface to handle user input
    if (containerForChoosing) {
      const formElement = document.querySelector(containerForChoosing);
      return formElement.querySelector(`#${elementId}`) as HTMLElement;
    }
    return document.getElementById(elementId);
  }

  ngAfterViewInit() {
    if (this.labelComponent) {
      const copyPasteState = this.config.UIData && this.config.UIData.copyPasteState;
      this.labelComponent.isCopyMode = copyPasteState && copyPasteState.visibility;
      this.copyClick$ = this.labelComponent.onCopyClick.subscribe(this.onCopyClickEvent);
      this.labelComponent.isDisabledGetter = this.isDisabled;
    }
    if (this.config && this.config.UIData && this.config.UIData.validation && this.config.UIData.validation.type) {
      // to handle onkeydown changes, for example
      const preInputValidator = getSpecialValidatorFn({
        type: this.config.UIData.validation.type,
        validationOptions: this.config.UIData.validation.options,
        onlyPreInput: true,
      });

      if (preInputValidator) {
        this.element = this.getControlElement();
        //TODO: if the metadata panel is collapsed, then no element will be found and this validation will not work...
        this.element && this.element.addEventListener('keydown', (ev) => this.filterInput(ev, preInputValidator));
      }
    }
  }

  private filterInput(ev: KeyboardEvent, validatorFn: SpecialValidatorFn) {
    if (!validatorFn) {
      return;
    }
    const control: FakeFormControl = {
      value: ev.key,
    };
    if (validatorFn(control)) {
      ev.preventDefault();
    }
  }

  public setStyleAutoHeight(element: HTMLElement, minHeightPx?: number): boolean {
    if (this.self.value) {
      // see TextAreaDirective resize() logic
      const initialHeightValue = +element.style.height.replace('px', '');
      let initialHeight = isNaN(initialHeightValue) ? 0 : initialHeightValue;

      if (initialHeight < minHeightPx) {
        initialHeight = minHeightPx;
      }

      element.style.height = `${initialHeight}px`;
      const scrollHeight = element.scrollHeight;
      if (scrollHeight > initialHeight) {
        element.style.height = `${scrollHeight}px`;
      }
    }
    return true;
  }

  clickFacet() {
    this.self.setValue(''); //hack for trigger facet valueChange
  }

  ngOnDestroy(): void {
    this.formChange$ && this.formChange$.unsubscribe();
    this.copyClick$ && this.copyClick$.unsubscribe();
    this.valueChange$ && this.valueChange$.unsubscribe();
    this.customChange$ && this.customChange$.unsubscribe();
    this.selfChange$ && this.selfChange$.unsubscribe();
  }

  public getError() {
    if (this.self && !this.self.valid) {
      if (this.self.hasError('required') && this.self.touched) {
        return this.baseComponentStrings.thisFieldIsRequired;
      }
      // we now use pattern validation for email, not a built-in .email validator
      if (this.self.hasError('email') || (Field.Is.requiredEmail(this.config.flags) && this.self.hasError('pattern'))) {
        return this.baseComponentStrings.shouldBeValidEmail;
      }
      if (this.self.hasError('maxError')) {
        return `${this.baseComponentStrings.maximumValueIs} ${this.self.errors.maxError.maxValue}`;
      }
      if (this.self.hasError('minError')) {
        return `${this.baseComponentStrings.minimumValueIs} ${this.self.errors.minError.minValue}`;
      }
      if (this.self.hasError('smallDate')) {
        return `${this.baseComponentStrings.dueDateCantBeLess}`;
      }
      if (this.self.hasError('previousFutureDates')) {
        return `${this.baseComponentStrings.contactDatesConfused}`;
      }
      if (this.self.hasError('dateBeforeMinField')) {
        return tiStringsFormat(
          this.baseComponentStrings.dateBeforeMinField,
          this.config.fieldTitle,
          this.config.UIData?.minDateFromFieldTitle ?? '',
        );
      }
      if (this.self.hasError('maxlength')) {
        return tiStringsFormat(tiStrings.app.misc.fieldMaximumLengthPattern, this.self.errors.maxlength.requiredLength);
      }
      if (this.self.hasError('irbAnyType')) {
        return this.baseComponentStrings.notAllowAnyType;
      }
      if (this.self.hasError('pattern')) {
        return tiStringsFormat(this.baseComponentStrings.patternError, this.config.fieldName);
      }
      // custom validators, can we improve it?
      const validationOptions =
        this.config.UIData &&
        this.config.UIData.validation &&
        this.config.UIData.validation.type &&
        this.config.UIData.validation.options;
      if (validationOptions) {
        if (this.self.hasError(SpecialValidationType.Range)) {
          return tiStringsFormat(
            this.baseComponentStrings[SpecialValidationType.Range],
            (<RangeOptions>validationOptions).from,
            (<RangeOptions>validationOptions).to,
          );
        }
        if (this.self.hasError(SpecialValidationType.AlphaConfigurable)) {
          return tiStringsFormat(
            this.baseComponentStrings[SpecialValidationType.AlphaConfigurable],
            (<AllowedCharsOptions>validationOptions).allowedChars,
          );
        }
      }
      if (this.self.hasError(SpecialValidationType.Alpha)) {
        return this.baseComponentStrings[SpecialValidationType.Alpha];
      }
      if (this.self.hasError(SpecialValidationType.AlphaNum)) {
        return this.baseComponentStrings[SpecialValidationType.AlphaNum];
      }
      if (this.self.hasError(SpecialValidationType.DenyFutureDates)) {
        return this.baseComponentStrings[SpecialValidationType.DenyFutureDates];
      }
      if (this.self.hasError(SpecialValidationType.DuplicateString)) {
        const options = this.config.UIData.validation.options as DuplicateStringOptions;
        return options?.isEventCopy
          ? this.baseComponentStrings.duplicateEventName
          : this.baseComponentStrings[SpecialValidationType.DuplicateString];
      }
      if (this.self.hasError(SpecialValidationType.DenyPastDates)) {
        const minDate = this.self.errors[SpecialValidationType.DenyPastDates];
        if (minDate) {
          return tiStringsFormat(
            this.baseComponentStrings[SpecialValidationType.DenyPastDates],
            this.roomDateFormatPipe.transform(minDate),
          );
        }
      }
    }

    return null;
  }

  public deselect(nullValue = null) {
    this.self.setValue(nullValue);
  }
}

@Component({
  standalone: false,
  selector: 'ti-form-input-base',
  styleUrls: ['./form-input-base.component.sass'],
  templateUrl: './form-input-base.component.html',
})
export class FormInputBaseComponent extends FormInputBase {
  @Input() name: string;
  @Input() config: FormFieldConfig;
  @Input() formGroup: UntypedFormGroup;
  @Input() valueChange$: Subscription;
  @Input() customChange$: Subscription;

  @Output() valueChangeEmitter = new EventEmitter();
  @Output() formRebuildRequestEmitter = new EventEmitter();
}

export interface FormInputBaseData {
  id: any;
  name: string;
}

@Directive()
export abstract class FormInputBaseDataComponent<T extends FormInputBaseData> extends FormInputBase implements OnInit {
  @Input() name: string;
  @Input() config: FormFieldConfig;
  @Input() formGroup: UntypedFormGroup;
  @Input() valueChange$: Subscription;
  @Input() customChange$: Subscription;

  @Output() valueChangeEmitter = new EventEmitter();
  @Output() formRebuildRequestEmitter = new EventEmitter();

  public data: T[];

  ngOnInit(): void {
    super.ngOnInit();

    if (this.self.value) {
      if (this.displayValue) {
        this.data = [this.getInitialDataItem(this.self.value, this.displayValue)];
      } else {
        this.loadData();
      }
    }
  }

  abstract getInitialDataItem(value: any, displayValue: string): T;

  abstract loadData(): void;
}

@Component({
  standalone: false,
  selector: 'ti-form-input-date-base',
  styleUrls: ['./form-input-base.component.sass'],
  templateUrl: './form-input-base.component.html',
})
export class FormInputDateBaseComponent extends FormInputBaseComponent {
  dateFormat: string = DateFormatStore.dateFormat;
  dateTimeFormat: string = DateFormatStore.dateTimeFormat;
  formatPlaceholder: DateInputFormatPlaceholder = DateFormatStore.getFormatPlaceholder;
}
